import { useI18n, LangToggle } from "@/shared/i18n";
import { trackNow } from "@/shared/analytics";
import "./landing.css";

const CONTACT_EMAIL = "garry@waterdroptech.co";
const COMPANY = "Water Drop Tech Limited";

/** Consistent 24px stroke icons (feather-style) — no emoji. */
const ICON_PROPS = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

function IconPen() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 11.5l2 2 4-4" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

function IconZap() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function Wordmark() {
  return (
    <span className="ld-wordmark">
      Human<b>Ink</b>
    </span>
  );
}

export function LandingPage() {
  const { t } = useI18n();

  return (
    <div className="ld">
      {/* ---- Nav ---- */}
      <nav className="ld-nav">
        <a className="ld-nav__brand" href="/">
          <span className="ld-nav__mark" aria-hidden />
          <Wordmark />
        </a>
        <div className="ld-nav__links">
          <a href="#features">{t("Product")}</a>
          <a href="#how">{t("How It Works")}</a>
          <a href="#contact">{t("Contact")}</a>
        </div>
        <div className="ld-nav__right">
          <LangToggle />
          <a
            className="ld-btn ld-btn--primary ld-btn--sm"
            href="/app"
            onClick={() => trackNow("cta_launch_app", { where: "nav" })}
          >
            {t("Launch App")}
          </a>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <header className="ld-hero">
        <div className="ld-hero__glow" aria-hidden />
        <span className="ld-badge">{t("Focused on humanized AI, by design")}</span>
        <h1 className="ld-hero__title">{t("Make AI Sound Human.")}</h1>
        <p className="ld-hero__sub">
          {t(
            "HumanInk is a purpose-built humanization model. It rewrites AI-generated text into prose with genuine human texture — while a fidelity guard keeps every number, name and fact intact.",
          )}
        </p>
        <div className="ld-hero__cta">
          <a
            className="ld-btn ld-btn--primary"
            href="/app"
            onClick={() => trackNow("cta_launch_app", { where: "hero" })}
          >
            {t("Start Humanizing")} <span aria-hidden>→</span>
          </a>
          <a className="ld-btn ld-btn--ghost" href={`mailto:${CONTACT_EMAIL}`}>
            {t("Contact Us")}
          </a>
        </div>

        <div className="ld-stats">
          <div className="ld-stat">
            <div className="ld-stat__num">Aa</div>
            <div className="ld-stat__label">{t("Natural wording")}</div>
          </div>
          <div className="ld-stat">
            <div className="ld-stat__num">✓</div>
            <div className="ld-stat__label">{t("Meaning checks")}</div>
          </div>
          <div className="ld-stat">
            <div className="ld-stat__num">1:1</div>
            <div className="ld-stat__label">{t("One credit per source word")}</div>
          </div>
        </div>
      </header>

      {/* ---- Features ---- */}
      <section className="ld-section" id="features">
        <h2 className="ld-section__title">{t("Why HumanInk")}</h2>
        <div className="ld-features">
          <article className="ld-card">
            <span className="ld-card__icon" aria-hidden><IconPen /></span>
            <h3>{t("Rewrite with a natural voice")}</h3>
            <p>
              {t(
                "HumanInk uses a model trained for rewriting to adjust sentence rhythm and wording while keeping your message in focus.",
              )}
            </p>
          </article>
          <article className="ld-card">
            <span className="ld-card__icon" aria-hidden><IconShield /></span>
            <h3>{t("Meaning comes first")}</h3>
            <p>
              {t(
                "Checks look for changes to numbers, percentages, names and meaning. A rewrite that fails the checks is not delivered as a successful result.",
              )}
            </p>
          </article>
          <article className="ld-card">
            <span className="ld-card__icon" aria-hidden><IconEyeOff /></span>
            <h3>{t("Review the result")}</h3>
            <p>
              {t(
                "Compare the rewrite with your source and check the tone, meaning and wording before you use it.",
              )}
            </p>

          </article>
          <article className="ld-card">
            <span className="ld-card__icon" aria-hidden><IconZap /></span>
            <h3>{t("Follow the progress")}</h3>
            <p>
              {t(
                "Follow the rewrite progress, then review the finished result. Processing time depends on the text and the checks it needs.",
              )}
            </p>
          </article>
        </div>
      </section>

      {/* ---- How it works ---- */}
      <section className="ld-section" id="how">
        <h2 className="ld-section__title">{t("Three steps to human")}</h2>
        <div className="ld-steps">
          <div className="ld-step">
            <span className="ld-step__num">1</span>
            <h3>{t("Paste your text")}</h3>
            <p>{t("Drop in AI-generated English text — up to 600 words per pass.")}</p>
          </div>
          <div className="ld-step">
            <span className="ld-step__num">2</span>
            <h3>{t("The model rewrites")}</h3>
            <p>
              {t(
                "The model rewrites your text and checks the result before marking it complete.",
              )}
            </p>
          </div>
          <div className="ld-step">
            <span className="ld-step__num">3</span>
            <h3>{t("Review and copy")}</h3>
            <p>
              {t(
                "Review the completed rewrite against your source, then copy the text when you are happy with it.",
              )}
            </p>
          </div>
        </div>
      </section>

      {/* ---- Bottom CTA ---- */}
      <section className="ld-cta">
        <h2>{t("Ready to make your AI text human?")}</h2>
        <p>{t("Free credits to start. No credit card required.")}</p>
        <a
          className="ld-btn ld-btn--primary"
          href="/app"
          onClick={() => trackNow("cta_launch_app", { where: "bottom" })}
        >
          {t("Start Humanizing")} <span aria-hidden>→</span>
        </a>
      </section>

      {/* ---- Footer ---- */}
      <footer className="ld-foot" id="contact">
        <div className="ld-foot__brand">
          <Wordmark />
          <p>{t("Vision: make AI human.")}</p>
        </div>
        <div className="ld-foot__meta">
          <span>{COMPANY}</span>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          <span>
            © {new Date().getFullYear()} {COMPANY}. {t("All rights reserved.")}
          </span>
        </div>
      </footer>
    </div>
  );
}
